import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

/**
 * Keeps an owner's half-finished listing form in the browser so a vendor who
 * leaves the page (or loses the OTP session) can pick up where they stopped.
 *
 * Drafts are keyed by listing type and the verified phone number. Every save
 * goes to the browser (so the form survives a refresh even offline) and, when
 * an owner token is supplied, to the server (so the admin can see what has
 * been filled in and the owner can resume on another device). On restore the
 * newer of the two copies wins. Files cannot be stored, so photos are the one
 * thing a vendor has to select again.
 *
 * Usage:
 *   const draft = useOwnerDraft({
 *     enabled: isOwnerMode && !!ownerPhone,
 *     type: 'hotel',                         // hotel | spa | tour | travel-package
 *     phone: ownerPhone,
 *     token: ownerToken,                     // optional: enables server sync
 *     data: { formData, roomTypes, ... },    // everything worth restoring
 *     onRestore: (saved) => { ...set state from saved... }
 *   });
 *
 *   draft.savedAt      - Date of the last write (null when nothing is saved)
 *   draft.restoredAt   - Date of the draft that was restored on mount, if any
 *   draft.synced       - false when the last server save failed (device-only)
 *   draft.save()       - write immediately (auto-save also runs on change)
 *   draft.clear()      - remove the draft (call after a successful submit)
 */

const STORAGE_PREFIX = 'weotp:owner-draft';
const AUTO_SAVE_DELAY_MS = 600;

export const ownerDraftKey = (type, phone) =>
  `${STORAGE_PREFIX}:${type}:${String(phone || '').replace(/\D/g, '')}`;

const safeStorage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

export const readOwnerDraft = (type, phone) => {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(ownerDraftKey(type, phone));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

export const clearOwnerDraft = (type, phone) => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(ownerDraftKey(type, phone));
  } catch (error) {
    // Ignore - a draft that cannot be removed is harmless
  }
};

/** Short "23 Sep, 11:02 am" stamp for save indicators. */
export const formatDraftTime = (date) => {
  if (!date) return '';
  try {
    return new Date(date).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch (error) {
    return String(date);
  }
};

/**
 * Admin side: load one owner draft by id so the "Add New ..." page can be
 * prefilled with what the vendor typed. `onApply` receives the raw draft data
 * (the same shape `useOwnerDraft` saves) and the draft record itself.
 *
 *   const prefill = useAdminDraftPrefill({ draftId, onApply: (data) => applyDraft(data) });
 *   prefill.draft   - { _id, phone, listingType, step, lastSavedAt, data } or null
 *   prefill.loading / prefill.error
 */
export const useAdminDraftPrefill = ({ draftId, onApply }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(Boolean(draftId));
  const [error, setError] = useState('');

  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  useEffect(() => {
    if (!draftId) {
      setDraft(null);
      setLoading(false);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    axios
      .get(`${API_BASE_URL}/admin/owner-registrations/drafts/${draftId}`)
      .then((res) => {
        if (cancelled) return;
        const record = res.data?.data;
        if (record && record.data && typeof record.data === 'object') {
          setDraft(record);
          if (onApplyRef.current) onApplyRef.current(record.data, record);
        } else {
          setError('Owner draft not found');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load the owner draft');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [draftId]);

  return { draft, loading, error };
};

/** Which listing types have a draft for this phone, e.g. ['hotel', 'spa']. */
export const listOwnerDrafts = (phone, types) =>
  types.filter((type) => Boolean(readOwnerDraft(type, phone)));

const remoteUrl = (type) => `${API_BASE_URL}/public/owners/drafts/${type}`;
const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const writeLocal = (type, phone, payload) => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(ownerDraftKey(type, phone), JSON.stringify(payload));
  } catch (error) {
    // Storage full or blocked - the server copy (if any) still works
  }
};

const useOwnerDraft = ({ enabled, type, phone, token = '', data, onRestore }) => {
  const [savedAt, setSavedAt] = useState(null);
  const [restoredAt, setRestoredAt] = useState(null);
  const [synced, setSynced] = useState(true);
  const [ready, setReady] = useState(false);

  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const dataRef = useRef(data);
  dataRef.current = data;

  const lastWrittenRef = useRef('');
  const timerRef = useRef(null);

  // Restore once per (type, phone): local copy, server copy, newest wins
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setRestoredAt(null);
    setSynced(true);
    lastWrittenRef.current = '';

    if (!enabled) return undefined;

    const finish = (chosen) => {
      if (cancelled) return;
      if (chosen) {
        const { savedAt: when, ...rest } = chosen;
        if (onRestoreRef.current) onRestoreRef.current(rest);
        const date = when ? new Date(when) : null;
        setSavedAt(date);
        setRestoredAt(date);
        lastWrittenRef.current = JSON.stringify(rest);
        // Keep the browser copy in step with whichever version was restored
        writeLocal(type, phone, chosen);
      } else {
        setSavedAt(null);
      }
      setReady(true);
    };

    const local = readOwnerDraft(type, phone);
    if (!token) {
      finish(local);
      return undefined;
    }

    axios
      .get(remoteUrl(type), authHeaders(token))
      .then((res) => {
        const remote = res.data?.data;
        const remoteDraft =
          remote && remote.data && typeof remote.data === 'object'
            ? { ...remote.data, savedAt: remote.savedAt }
            : null;
        if (!remoteDraft) return finish(local);
        if (!local) return finish(remoteDraft);
        const localTime = new Date(local.savedAt || 0).getTime();
        const remoteTime = new Date(remoteDraft.savedAt || 0).getTime();
        return finish(remoteTime >= localTime ? remoteDraft : local);
      })
      .catch(() => {
        setSynced(false);
        finish(local);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, type, phone, token]);

  const writeNow = useCallback(() => {
    if (!enabled) return;

    const snapshot = dataRef.current;
    const serialized = JSON.stringify(snapshot);
    if (serialized === lastWrittenRef.current) return;

    const now = new Date();
    writeLocal(type, phone, { ...snapshot, savedAt: now.toISOString() });
    lastWrittenRef.current = serialized;
    setSavedAt(now);

    if (token) {
      axios
        .put(remoteUrl(type), { data: snapshot, step: snapshot.activeTab || '' }, authHeaders(token))
        .then(() => setSynced(true))
        .catch(() => setSynced(false));
    }
  }, [enabled, type, phone, token]);

  // Auto-save shortly after any change, once the restore has happened
  const serializedData = JSON.stringify(data);
  useEffect(() => {
    if (!enabled || !ready) return undefined;
    if (serializedData === lastWrittenRef.current) return undefined;

    timerRef.current = setTimeout(writeNow, AUTO_SAVE_DELAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [enabled, ready, serializedData, writeNow]);

  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
    clearOwnerDraft(type, phone);
    lastWrittenRef.current = '';
    setSavedAt(null);
    setRestoredAt(null);
    if (token) {
      axios.delete(remoteUrl(type), authHeaders(token)).catch(() => {});
    }
  }, [type, phone, token]);

  return { savedAt, restoredAt, synced, save: writeNow, clear };
};

export default useOwnerDraft;
